// Copyright Square, Inc.

// Generated using eventGen — https://github.com/tidal-engineering/experimentation-cdf
// Do not edit directly!

/*
 */
export type SubscriptionSubscriptionType = 'INTRO' | 'PREMIUM' | 'PREMIUM_PLUS';

/*
 */
export type SubscriptionSubscriptionPlan =
  | 'FAMILY'
  | 'FIRST_RESPONDER'
  | 'MILITARY'
  | 'STANDARD'
  | 'STUDENT';

/*
 */
export type SubscriptionLoginMethod =
  | 'apple'
  | 'email'
  | 'facebook'
  | 'google'
  | 'twitter';

/*
 */
export type SubscriptionAddOnType = 'Dj';

/**
 * @see SubscriptionStartViewTidalComPageEvent
 */
export type SubscriptionStartViewTidalComPageParams = {
  /**
   * UTM ad ID.
   */
  adId: string;
  /**
   * UTM ad position.
   */
  adPosition: string;
  /**
   * UTM adset ID.
   */
  adsetId: string;
  /**
   * UTM adset name.
   */
  adsetName: string;
  /**
   * The UTM campaign ID provides the details of the channel, campaign name, etc, allowing us to attribute the user visit to that original location.
   */
  campaignId: string;
  /**
   * UTM campaign type.
   */
  campaignType: string;
  /**
   * Current route.
   */
  currentRoute: string;
  /**
   * UTM placement. Page from where the user is coming from.
   */
  placement: string;
  /**
   * Previous route within service scope.
   */
  previousRoute: string;
  /**
   * UTM search query passes the exact keyword string the user input into the search engine.
   */
  query: string;
  /**
   * Internal or external page from where the user is coming from.
   */
  referrer: string;
  /**
   * The unique campaign name for a promotion and or channel.
   */
  utmCampaign: string;
  /**
   * Used to differentiate similar content, or links within the same ad. For example, if you have two call-to-action links within the same email message, you can use utm_content and set different values for each so you can tell which version is more effective.
   */
  utmContent: string;
  /**
   * The advertising or marketing medium, for example: cpc, banner, email newsletter.
   */
  utmMedium: string;
  /**
   * Identify the advertiser, site, publication, etc. that is sending traffic to your property, for example Paid Search, Paid social, email, etc.
   */
  utmSource: string;
  /**
   * The unique campaign name for a promotion and or channel.
   */
  utmTerm: string;
};

/**
 * Subscription Start ViewTidalComPage Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: Start - When a user navigates through TIDAL landing pages.
 *
 * **Event**: ViewTidalComPage - Triggers when a user lands on www.tidal.com
 */
export type SubscriptionStartViewTidalComPageEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_Start_ViewTidalComPage';
  parameters: {
    cdf_action: 'Start';
    cdf_entity: 'Subscription';
  } & SubscriptionStartViewTidalComPageParams;
  version: 1;
};

/**
 * @see SubscriptionStartViewTidalComPageEvent
 */
export const SubscriptionStartViewTidalComPage = ({
  adId,
  adPosition,
  adsetId,
  adsetName,
  campaignId,
  campaignType,
  currentRoute,
  placement,
  previousRoute,
  query,
  referrer,
  utmCampaign,
  utmContent,
  utmMedium,
  utmSource,
  utmTerm,
}: SubscriptionStartViewTidalComPageParams): SubscriptionStartViewTidalComPageEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_Start_ViewTidalComPage',
  parameters: {
    adId: adId,
    adPosition: adPosition,
    adsetId: adsetId,
    adsetName: adsetName,
    campaignId: campaignId,
    campaignType: campaignType,
    cdf_action: 'Start',
    cdf_entity: 'Subscription',
    currentRoute: currentRoute,
    placement: placement,
    previousRoute: previousRoute,
    query: query,
    referrer: referrer,
    utmCampaign: utmCampaign,
    utmContent: utmContent,
    utmMedium: utmMedium,
    utmSource: utmSource,
    utmTerm: utmTerm,
  },
  version: 1,
});

/**
 * @see SubscriptionStartViewCampaignPageEvent
 */
export type SubscriptionStartViewCampaignPageParams = {
  /**
   * UTM ad ID.
   */
  adId: string;
  /**
   * UTM ad position.
   */
  adPosition: string;
  /**
   * UTM adset ID.
   */
  adsetId: string;
  /**
   * UTM adset name.
   */
  adsetName: string;
  /**
   * The UTM campaign ID provides the details of the channel, campaign name, etc, allowing us to attribute the user visit to that original location.
   */
  campaignId: string;
  /**
   * UTM campaign type.
   */
  campaignType: string;
  /**
   * Current route.
   */
  currentRoute: string;
  /**
   * UTM placement. Page from where the user is coming from.
   */
  placement: string;
  /**
   * Previous route within service scope.
   */
  previousRoute: string;
  /**
   * UTM search query passes the exact keyword string the user input into the search engine.
   */
  query: string;
  /**
   * Internal or external page from where the user is coming from.
   */
  referrer: string;
  /**
   * Xadmin campaign id.
   */
  tidalCampaignId: number;
  /**
   * The unique campaign name for a promotion and or channel.
   */
  utmCampaign: string;
  /**
   * Used to differentiate similar content, or links within the same ad. For example, if you have two call-to-action links within the same email message, you can use utm_content and set different values for each so you can tell which version is more effective.
   */
  utmContent: string;
  /**
   * The advertising or marketing medium, for example: cpc, banner, email newsletter.
   */
  utmMedium: string;
  /**
   * Identify the advertiser, site, publication, etc. that is sending traffic to your property, for example Paid Search, Paid social, email, etc.
   */
  utmSource: string;
  /**
   * The unique campaign name for a promotion and or channel.
   */
  utmTerm: string;
};

/**
 * Subscription Start ViewCampaignPage Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: Start - When a user navigates through TIDAL landing pages.
 *
 * **Event**: ViewCampaignPage - Triggers when a user lands on www.offer.tidal.com/campaigns/[campaignId]
 */
export type SubscriptionStartViewCampaignPageEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_Start_ViewCampaignPage';
  parameters: {
    cdf_action: 'Start';
    cdf_entity: 'Subscription';
  } & SubscriptionStartViewCampaignPageParams;
  version: 1;
};

/**
 * @see SubscriptionStartViewCampaignPageEvent
 */
export const SubscriptionStartViewCampaignPage = ({
  adId,
  adPosition,
  adsetId,
  adsetName,
  campaignId,
  campaignType,
  currentRoute,
  placement,
  previousRoute,
  query,
  referrer,
  tidalCampaignId,
  utmCampaign,
  utmContent,
  utmMedium,
  utmSource,
  utmTerm,
}: SubscriptionStartViewCampaignPageParams): SubscriptionStartViewCampaignPageEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_Start_ViewCampaignPage',
  parameters: {
    adId: adId,
    adPosition: adPosition,
    adsetId: adsetId,
    adsetName: adsetName,
    campaignId: campaignId,
    campaignType: campaignType,
    cdf_action: 'Start',
    cdf_entity: 'Subscription',
    currentRoute: currentRoute,
    placement: placement,
    previousRoute: previousRoute,
    query: query,
    referrer: referrer,
    tidalCampaignId: tidalCampaignId,
    utmCampaign: utmCampaign,
    utmContent: utmContent,
    utmMedium: utmMedium,
    utmSource: utmSource,
    utmTerm: utmTerm,
  },
  version: 1,
});

/**
 * @see SubscriptionStartGetStartedEvent
 */
export type SubscriptionStartGetStartedParams = {
  /**
   * Which button the user clicked on.
   */
  comingFrom: string;
};

/**
 * Subscription Start GetStarted Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: Start - When a user navigates through TIDAL landing pages.
 *
 * **Event**: GetStarted - Triggers when a user clicks 'start free trial' in a landing page.
 */
export type SubscriptionStartGetStartedEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_Start_GetStarted';
  parameters: {
    cdf_action: 'Start';
    cdf_entity: 'Subscription';
  } & SubscriptionStartGetStartedParams;
  version: 1;
};

/**
 * @see SubscriptionStartGetStartedEvent
 */
export const SubscriptionStartGetStarted = ({
  comingFrom,
}: SubscriptionStartGetStartedParams): SubscriptionStartGetStartedEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_Start_GetStarted',
  parameters: {
    cdf_action: 'Start',
    cdf_entity: 'Subscription',
    comingFrom: comingFrom,
  },
  version: 1,
});

/**
 * @see SubscriptionLoginOrRegisterNativeSocialEvent
 */
export type SubscriptionLoginOrRegisterNativeSocialParams = {
  /**
   * Whether the user is registering as apposed to just logging in.
   */
  isRegistration: boolean;
  /**
   * Which method the user is using to login or register.
   */
  loginMethod: SubscriptionLoginMethod;
};

/**
 * Subscription LoginOrRegister NativeSocial Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: LoginOrRegister - When a user tries to authenticate, at this point it could be login or registration.
 *
 * **Event**: NativeSocial - Triggers when user follows a natively implemented social login method to login or register.
 */
export type SubscriptionLoginOrRegisterNativeSocialEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_LoginOrRegister_NativeSocial';
  parameters: {
    cdf_action: 'LoginOrRegister';
    cdf_entity: 'Subscription';
  } & SubscriptionLoginOrRegisterNativeSocialParams;
  version: 1;
};

/**
 * @see SubscriptionLoginOrRegisterNativeSocialEvent
 */
export const SubscriptionLoginOrRegisterNativeSocial = ({
  isRegistration,
  loginMethod,
}: SubscriptionLoginOrRegisterNativeSocialParams): SubscriptionLoginOrRegisterNativeSocialEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_LoginOrRegister_NativeSocial',
  parameters: {
    cdf_action: 'LoginOrRegister',
    cdf_entity: 'Subscription',
    isRegistration: isRegistration,
    loginMethod: loginMethod,
  },
  version: 1,
});

/**
 * @see SubscriptionLoginOrRegisterViewPageEvent
 */
export type SubscriptionLoginOrRegisterViewPageParams = {
  /**
   * The view mode of the app affecting layout.
   */
  appMode?: string;
  /**
   * Current route.
   */
  currentRoute: string;
  /**
   * Previous route within service scope.
   */
  previousRoute: string;
  /**
   * URL the user will be redirected to after finishing login or registration. Includes hostname + pathname, but not query parameters as they may contain sensitive data.
   */
  returnUrl: string;
};

/**
 * Subscription LoginOrRegister ViewPage Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: LoginOrRegister - When a user tries to authenticate, at this point it could be login or registration.
 *
 * **Event**: ViewPage - Triggers when a user lands on login.tidal.com/authorize
 */
export type SubscriptionLoginOrRegisterViewPageEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_LoginOrRegister_ViewPage';
  parameters: {
    cdf_action: 'LoginOrRegister';
    cdf_entity: 'Subscription';
  } & SubscriptionLoginOrRegisterViewPageParams;
  version: 1;
};

/**
 * @see SubscriptionLoginOrRegisterViewPageEvent
 */
export const SubscriptionLoginOrRegisterViewPage = ({
  appMode,
  currentRoute,
  previousRoute,
  returnUrl,
}: SubscriptionLoginOrRegisterViewPageParams): SubscriptionLoginOrRegisterViewPageEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_LoginOrRegister_ViewPage',
  parameters: {
    appMode: appMode,
    cdf_action: 'LoginOrRegister',
    cdf_entity: 'Subscription',
    currentRoute: currentRoute,
    previousRoute: previousRoute,
    returnUrl: returnUrl,
  },
  version: 1,
});

/**
 * @see SubscriptionLoginOrRegisterContinueEvent
 */
export type SubscriptionLoginOrRegisterContinueParams = {
  /**
   * The view mode of the app affecting layout.
   */
  appMode?: string;
  /**
   * Which method the user is using to login.
   */
  loginMethod: SubscriptionLoginMethod;
};

/**
 * Subscription LoginOrRegister Continue Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: LoginOrRegister - When a user tries to authenticate, at this point it could be login or registration.
 *
 * **Event**: Continue - Triggers when a user clicks continue after entering their email in /authorize page.
 */
export type SubscriptionLoginOrRegisterContinueEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_LoginOrRegister_Continue';
  parameters: {
    cdf_action: 'LoginOrRegister';
    cdf_entity: 'Subscription';
  } & SubscriptionLoginOrRegisterContinueParams;
  version: 1;
};

/**
 * @see SubscriptionLoginOrRegisterContinueEvent
 */
export const SubscriptionLoginOrRegisterContinue = ({
  appMode,
  loginMethod,
}: SubscriptionLoginOrRegisterContinueParams): SubscriptionLoginOrRegisterContinueEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_LoginOrRegister_Continue',
  parameters: {
    appMode: appMode,
    cdf_action: 'LoginOrRegister',
    cdf_entity: 'Subscription',
    loginMethod: loginMethod,
  },
  version: 1,
});

/**
 * @see SubscriptionRegisterSelectDOBEvent
 */
export type SubscriptionRegisterSelectDOBParams = {
  /**
   * The view mode of the app affecting layout.
   */
  appMode?: string;
  /**
   * Year of birth. Only sent if the user is over 18 years of age.
   */
  year: number;
};

/**
 * Subscription Register SelectDOB Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: Register - When a user tries to create a TIDAL account.
 *
 * **Event**: SelectDOB - Triggers when a user selects DOB.
 */
export type SubscriptionRegisterSelectDOBEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_Register_SelectDOB';
  parameters: {
    cdf_action: 'Register';
    cdf_entity: 'Subscription';
  } & SubscriptionRegisterSelectDOBParams;
  version: 1;
};

/**
 * @see SubscriptionRegisterSelectDOBEvent
 */
export const SubscriptionRegisterSelectDOB = ({
  appMode,
  year,
}: SubscriptionRegisterSelectDOBParams): SubscriptionRegisterSelectDOBEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_Register_SelectDOB',
  parameters: {
    appMode: appMode,
    cdf_action: 'Register',
    cdf_entity: 'Subscription',
    year: year,
  },
  version: 1,
});

/**
 * @see SubscriptionRegisterConfirmEvent
 */
export type SubscriptionRegisterConfirmParams = {
  /**
   * The view mode of the app affecting layout.
   */
  appMode?: string;
};

/**
 * Subscription Register Confirm Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: Register - When a user tries to create a TIDAL account.
 *
 * **Event**: Confirm - Triggers when a user clicks 'Sign Up' button and confirms registration.
 */
export type SubscriptionRegisterConfirmEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_Register_Confirm';
  parameters: {
    cdf_action: 'Register';
    cdf_entity: 'Subscription';
  } & SubscriptionRegisterConfirmParams;
  version: 1;
};

/**
 * @see SubscriptionRegisterConfirmEvent
 */
export const SubscriptionRegisterConfirm = ({
  appMode,
}: SubscriptionRegisterConfirmParams): SubscriptionRegisterConfirmEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_Register_Confirm',
  parameters: {
    appMode: appMode,
    cdf_action: 'Register',
    cdf_entity: 'Subscription',
  },
  version: 1,
});

/**
 * @see SubscriptionRegisterCompleteEvent
 */
export type SubscriptionRegisterCompleteParams = {
  /**
   * The view mode of the app affecting layout.
   */
  appMode?: string;
};

/**
 * Subscription Register Complete Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: Register - When a user tries to create a TIDAL account.
 *
 * **Event**: Complete - Triggers after user registration success.
 */
export type SubscriptionRegisterCompleteEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_Register_Complete';
  parameters: {
    cdf_action: 'Register';
    cdf_entity: 'Subscription';
  } & SubscriptionRegisterCompleteParams;
  version: 1;
};

/**
 * @see SubscriptionRegisterCompleteEvent
 */
export const SubscriptionRegisterComplete = ({
  appMode,
}: SubscriptionRegisterCompleteParams): SubscriptionRegisterCompleteEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_Register_Complete',
  parameters: {
    appMode: appMode,
    cdf_action: 'Register',
    cdf_entity: 'Subscription',
  },
  version: 1,
});

/**
 * @see SubscriptionLoginConfirmEvent
 */
export type SubscriptionLoginConfirmParams = {
  /**
   * The view mode of the app affecting layout.
   */
  appMode?: string;
};

/**
 * Subscription Login Confirm Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: Login - When a user tries to access TIDAL with an existing account.
 *
 * **Event**: Confirm - Triggers when a user clicks 'Log In' button and confirms login.
 */
export type SubscriptionLoginConfirmEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_Login_Confirm';
  parameters: {
    cdf_action: 'Login';
    cdf_entity: 'Subscription';
  } & SubscriptionLoginConfirmParams;
  version: 1;
};

/**
 * @see SubscriptionLoginConfirmEvent
 */
export const SubscriptionLoginConfirm = ({
  appMode,
}: SubscriptionLoginConfirmParams): SubscriptionLoginConfirmEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_Login_Confirm',
  parameters: {
    appMode: appMode,
    cdf_action: 'Login',
    cdf_entity: 'Subscription',
  },
  version: 1,
});

/**
 * @see SubscriptionLoginCompleteEvent
 */
export type SubscriptionLoginCompleteParams = {
  /**
   * The view mode of the app affecting layout.
   */
  appMode?: string;
};

/**
 * Subscription Login Complete Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: Login - When a user tries to access TIDAL with an existing account.
 *
 * **Event**: Complete - Triggers after user login success.
 */
export type SubscriptionLoginCompleteEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_Login_Complete';
  parameters: {
    cdf_action: 'Login';
    cdf_entity: 'Subscription';
  } & SubscriptionLoginCompleteParams;
  version: 1;
};

/**
 * @see SubscriptionLoginCompleteEvent
 */
export const SubscriptionLoginComplete = ({
  appMode,
}: SubscriptionLoginCompleteParams): SubscriptionLoginCompleteEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_Login_Complete',
  parameters: {
    appMode: appMode,
    cdf_action: 'Login',
    cdf_entity: 'Subscription',
  },
  version: 1,
});

/**
 * @see SubscriptionSelectProductViewPageEvent
 */
export type SubscriptionSelectProductViewPageParams = {
  /**
   * Current route.
   */
  currentRoute?: string;
  /**
   * Previous route within service scope.
   */
  previousRoute?: string;
};

/**
 * Subscription SelectProduct ViewPage Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: SelectProduct - When a user navigates through our product selector choosing a subscription plan and type.
 *
 * **Event**: ViewPage - Triggers when a user lands on offer.tidal.com/products or offer.tidal.com/campaigns/<campaignId>/products
 */
export type SubscriptionSelectProductViewPageEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_SelectProduct_ViewPage';
  parameters: {
    cdf_action: 'SelectProduct';
    cdf_entity: 'Subscription';
  } & SubscriptionSelectProductViewPageParams;
  version: 1;
};

/**
 * @see SubscriptionSelectProductViewPageEvent
 */
export const SubscriptionSelectProductViewPage = ({
  currentRoute,
  previousRoute,
}: SubscriptionSelectProductViewPageParams): SubscriptionSelectProductViewPageEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_SelectProduct_ViewPage',
  parameters: {
    cdf_action: 'SelectProduct',
    cdf_entity: 'Subscription',
    currentRoute: currentRoute,
    previousRoute: previousRoute,
  },
  version: 1,
});

/**
 * @see SubscriptionSelectProductSelectTypeEvent
 */
export type SubscriptionSelectProductSelectTypeParams = {
  /**
   * TIDAL subscription type.
   */
  subscriptionType: SubscriptionSubscriptionType;
};

/**
 * Subscription SelectProduct SelectType Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: SelectProduct - When a user navigates through our product selector choosing a subscription plan and type.
 *
 * **Event**: SelectType - Triggers when a user selects a subscription type.
 */
export type SubscriptionSelectProductSelectTypeEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_SelectProduct_SelectType';
  parameters: {
    cdf_action: 'SelectProduct';
    cdf_entity: 'Subscription';
  } & SubscriptionSelectProductSelectTypeParams;
  version: 1;
};

/**
 * @see SubscriptionSelectProductSelectTypeEvent
 */
export const SubscriptionSelectProductSelectType = ({
  subscriptionType,
}: SubscriptionSelectProductSelectTypeParams): SubscriptionSelectProductSelectTypeEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_SelectProduct_SelectType',
  parameters: {
    cdf_action: 'SelectProduct',
    cdf_entity: 'Subscription',
    subscriptionType: subscriptionType,
  },
  version: 1,
});

/**
 * @see SubscriptionSelectProductSelectPlanEvent
 */
export type SubscriptionSelectProductSelectPlanParams = {
  /**
   * TIDAL subscription plan.
   */
  subscriptionPlan: SubscriptionSubscriptionPlan;
};

/**
 * Subscription SelectProduct SelectPlan Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: SelectProduct - When a user navigates through our product selector choosing a subscription plan and type.
 *
 * **Event**: SelectPlan - Triggers when a user selects a subscription plan.
 */
export type SubscriptionSelectProductSelectPlanEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_SelectProduct_SelectPlan';
  parameters: {
    cdf_action: 'SelectProduct';
    cdf_entity: 'Subscription';
  } & SubscriptionSelectProductSelectPlanParams;
  version: 1;
};

/**
 * @see SubscriptionSelectProductSelectPlanEvent
 */
export const SubscriptionSelectProductSelectPlan = ({
  subscriptionPlan,
}: SubscriptionSelectProductSelectPlanParams): SubscriptionSelectProductSelectPlanEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_SelectProduct_SelectPlan',
  parameters: {
    cdf_action: 'SelectProduct',
    cdf_entity: 'Subscription',
    subscriptionPlan: subscriptionPlan,
  },
  version: 1,
});

/**
 * @see SubscriptionSelectProductConfirmEvent
 */
export type SubscriptionSelectProductConfirmParams = {
  /**
   * True if DJ add-on enabled.
   */
  djAddOnEnabled: boolean;
  /**
   * TIDAL subscription plan.
   */
  subscriptionPlan: SubscriptionSubscriptionPlan;
  /**
   * TIDAL subscription type.
   */
  subscriptionType?: SubscriptionSubscriptionType;
  /**
   * Xadmin campaign id.
   */
  tidalCampaignId?: number;
};

/**
 * Subscription SelectProduct Confirm Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: SelectProduct - When a user navigates through our product selector choosing a subscription plan and type.
 *
 * **Event**: Confirm - Triggers when a user clicks continue after confirming a product selection.
 */
export type SubscriptionSelectProductConfirmEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_SelectProduct_Confirm';
  parameters: {
    cdf_action: 'SelectProduct';
    cdf_entity: 'Subscription';
  } & SubscriptionSelectProductConfirmParams;
  version: 1;
};

/**
 * @see SubscriptionSelectProductConfirmEvent
 */
export const SubscriptionSelectProductConfirm = ({
  djAddOnEnabled,
  subscriptionPlan,
  subscriptionType,
  tidalCampaignId,
}: SubscriptionSelectProductConfirmParams): SubscriptionSelectProductConfirmEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_SelectProduct_Confirm',
  parameters: {
    cdf_action: 'SelectProduct',
    cdf_entity: 'Subscription',
    djAddOnEnabled: djAddOnEnabled,
    subscriptionPlan: subscriptionPlan,
    subscriptionType: subscriptionType,
    tidalCampaignId: tidalCampaignId,
  },
  version: 1,
});

/**
 * @see SubscriptionSelectProductSelectAddOnEvent
 */
export type SubscriptionSelectProductSelectAddOnParams = {
  /**
   * Add-on type.
   */
  addOnType: SubscriptionAddOnType;
};

/**
 * Subscription SelectProduct SelectAddOn Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: SelectProduct - When a user navigates through our product selector choosing a subscription plan and type.
 *
 * **Event**: SelectAddOn - Triggers when a user switchs on an add-on.
 */
export type SubscriptionSelectProductSelectAddOnEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_SelectProduct_SelectAddOn';
  parameters: {
    cdf_action: 'SelectProduct';
    cdf_entity: 'Subscription';
  } & SubscriptionSelectProductSelectAddOnParams;
  version: 1;
};

/**
 * @see SubscriptionSelectProductSelectAddOnEvent
 */
export const SubscriptionSelectProductSelectAddOn = ({
  addOnType,
}: SubscriptionSelectProductSelectAddOnParams): SubscriptionSelectProductSelectAddOnEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_SelectProduct_SelectAddOn',
  parameters: {
    addOnType: addOnType,
    cdf_action: 'SelectProduct',
    cdf_entity: 'Subscription',
  },
  version: 1,
});

/**
 * @see SubscriptionSelectProductRemoveAddOnEvent
 */
export type SubscriptionSelectProductRemoveAddOnParams = {
  /**
   * Add-on type.
   */
  addOnType: SubscriptionAddOnType;
};

/**
 * Subscription SelectProduct RemoveAddOn Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: SelectProduct - When a user navigates through our product selector choosing a subscription plan and type.
 *
 * **Event**: RemoveAddOn - Triggers when a user switchs off an add-on.
 */
export type SubscriptionSelectProductRemoveAddOnEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_SelectProduct_RemoveAddOn';
  parameters: {
    cdf_action: 'SelectProduct';
    cdf_entity: 'Subscription';
  } & SubscriptionSelectProductRemoveAddOnParams;
  version: 1;
};

/**
 * @see SubscriptionSelectProductRemoveAddOnEvent
 */
export const SubscriptionSelectProductRemoveAddOn = ({
  addOnType,
}: SubscriptionSelectProductRemoveAddOnParams): SubscriptionSelectProductRemoveAddOnEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_SelectProduct_RemoveAddOn',
  parameters: {
    addOnType: addOnType,
    cdf_action: 'SelectProduct',
    cdf_entity: 'Subscription',
  },
  version: 1,
});

/**
 * @see SubscriptionPayViewPageEvent
 */
export type SubscriptionPayViewPageParams = {
  /**
   * Current route.
   */
  currentRoute: string;
  /**
   * Previous route within service scope.
   */
  previousRoute: string;
};

/**
 * Subscription Pay ViewPage Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: Pay - When a user enter their payment details to start a subscription.
 *
 * **Event**: ViewPage - Triggers when a user lands on payment.tidal.com/subscription-order/<subscriptionId>
 */
export type SubscriptionPayViewPageEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_Pay_ViewPage';
  parameters: {
    cdf_action: 'Pay';
    cdf_entity: 'Subscription';
  } & SubscriptionPayViewPageParams;
  version: 1;
};

/**
 * @see SubscriptionPayViewPageEvent
 */
export const SubscriptionPayViewPage = ({
  currentRoute,
  previousRoute,
}: SubscriptionPayViewPageParams): SubscriptionPayViewPageEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_Pay_ViewPage',
  parameters: {
    cdf_action: 'Pay',
    cdf_entity: 'Subscription',
    currentRoute: currentRoute,
    previousRoute: previousRoute,
  },
  version: 1,
});

/**
 * @see SubscriptionPaySelectMethodEvent
 */
export type SubscriptionPaySelectMethodParams = {
  /**
   *
   */
  paymentMethod: SubscriptionPaySelectMethodPaymentMethod;
};

/**
 * Subscription Pay SelectMethod Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: Pay - When a user enter their payment details to start a subscription.
 *
 * **Event**: SelectMethod - Triggers when a user selects a payment method.
 */
export type SubscriptionPaySelectMethodEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_Pay_SelectMethod';
  parameters: {
    cdf_action: 'Pay';
    cdf_entity: 'Subscription';
  } & SubscriptionPaySelectMethodParams;
  version: 1;
};

/**
 * @see SubscriptionPaySelectMethodEvent
 */
export const SubscriptionPaySelectMethod = ({
  paymentMethod,
}: SubscriptionPaySelectMethodParams): SubscriptionPaySelectMethodEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_Pay_SelectMethod',
  parameters: {
    cdf_action: 'Pay',
    cdf_entity: 'Subscription',
    paymentMethod: paymentMethod,
  },
  version: 1,
});

/*
 */
export type SubscriptionPaySelectMethodPaymentMethod =
  | 'ADYEN_BOLETO'
  | 'ADYEN_CREDIT_CARD'
  | 'ADYEN_IDEAL'
  | 'ADYEN_SOFORT'
  | 'APPLE_PAY'
  | 'CASH_APP'
  | 'FORTUMO'
  | 'GOOGLE_PLAY_BILLING'
  | 'PAYPAL'
  | 'VENMO'
  | 'VIVO';

/**
 * Subscription Pay Confirm Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: Pay - When a user enter their payment details to start a subscription.
 *
 * **Event**: Confirm - Triggers when a user clicks continue and confirm payment.
 */
export type SubscriptionPayConfirmEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_Pay_Confirm';
  parameters: {
    cdf_action: 'Pay';
    cdf_entity: 'Subscription';
  };
  version: 1;
};

/**
 * @see SubscriptionPayConfirmEvent
 */
export const SubscriptionPayConfirm = (): SubscriptionPayConfirmEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_Pay_Confirm',
  parameters: {
    cdf_action: 'Pay',
    cdf_entity: 'Subscription',
  },
  version: 1,
});

/**
 * Subscription Pay Complete Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: Pay - When a user enter their payment details to start a subscription.
 *
 * **Event**: Complete - Triggers after payment success.
 */
export type SubscriptionPayCompleteEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_Pay_Complete';
  parameters: {
    cdf_action: 'Pay';
    cdf_entity: 'Subscription';
  };
  version: 1;
};

/**
 * @see SubscriptionPayCompleteEvent
 */
export const SubscriptionPayComplete = (): SubscriptionPayCompleteEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_Pay_Complete',
  parameters: {
    cdf_action: 'Pay',
    cdf_entity: 'Subscription',
  },
  version: 1,
});

/**
 * @see SubscriptionDownloadViewPageEvent
 */
export type SubscriptionDownloadViewPageParams = {
  /**
   * Current route.
   */
  currentRoute: string;
  /**
   * Previous route within service scope.
   */
  previousRoute: string;
};

/**
 * Subscription Download ViewPage Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: Download - When a user clicks on download button.
 *
 * **Event**: ViewPage - Triggers when a user lands on offer.tidal.com/download
 */
export type SubscriptionDownloadViewPageEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_Download_ViewPage';
  parameters: {
    cdf_action: 'Download';
    cdf_entity: 'Subscription';
  } & SubscriptionDownloadViewPageParams;
  version: 1;
};

/**
 * @see SubscriptionDownloadViewPageEvent
 */
export const SubscriptionDownloadViewPage = ({
  currentRoute,
  previousRoute,
}: SubscriptionDownloadViewPageParams): SubscriptionDownloadViewPageEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_Download_ViewPage',
  parameters: {
    cdf_action: 'Download',
    cdf_entity: 'Subscription',
    currentRoute: currentRoute,
    previousRoute: previousRoute,
  },
  version: 1,
});

/**
 * @see SubscriptionDownloadSelectPlatformEvent
 */
export type SubscriptionDownloadSelectPlatformParams = {
  /**
   * TIDAL platform / client.
   */
  platform: SubscriptionDownloadSelectPlatformPlatform;
};

/**
 * Subscription Download SelectPlatform Event
 *
 * **Entity**: Subscription - Refers to a TIDAL subscription.
 *
 * **Action**: Download - When a user clicks on download button.
 *
 * **Event**: SelectPlatform - Triggers when a user clicks download app button.
 */
export type SubscriptionDownloadSelectPlatformEvent = {
  consentCategory: 'PERFORMANCE';
  group: 'onboarding';
  name: 'Subscription_Download_SelectPlatform';
  parameters: {
    cdf_action: 'Download';
    cdf_entity: 'Subscription';
  } & SubscriptionDownloadSelectPlatformParams;
  version: 1;
};

/**
 * @see SubscriptionDownloadSelectPlatformEvent
 */
export const SubscriptionDownloadSelectPlatform = ({
  platform,
}: SubscriptionDownloadSelectPlatformParams): SubscriptionDownloadSelectPlatformEvent => ({
  consentCategory: 'PERFORMANCE',
  group: 'onboarding',
  name: 'Subscription_Download_SelectPlatform',
  parameters: {
    cdf_action: 'Download',
    cdf_entity: 'Subscription',
    platform: platform,
  },
  version: 1,
});

/*
 */
export type SubscriptionDownloadSelectPlatformPlatform =
  | 'android'
  | 'iOS'
  | 'macOS'
  | 'windows';

export type SubscriptionEvents =
  | SubscriptionDownloadSelectPlatformEvent
  | SubscriptionDownloadViewPageEvent
  | SubscriptionLoginCompleteEvent
  | SubscriptionLoginConfirmEvent
  | SubscriptionLoginOrRegisterContinueEvent
  | SubscriptionLoginOrRegisterNativeSocialEvent
  | SubscriptionLoginOrRegisterViewPageEvent
  | SubscriptionPayCompleteEvent
  | SubscriptionPayConfirmEvent
  | SubscriptionPaySelectMethodEvent
  | SubscriptionPayViewPageEvent
  | SubscriptionRegisterCompleteEvent
  | SubscriptionRegisterConfirmEvent
  | SubscriptionRegisterSelectDOBEvent
  | SubscriptionSelectProductConfirmEvent
  | SubscriptionSelectProductRemoveAddOnEvent
  | SubscriptionSelectProductSelectAddOnEvent
  | SubscriptionSelectProductSelectPlanEvent
  | SubscriptionSelectProductSelectTypeEvent
  | SubscriptionSelectProductViewPageEvent
  | SubscriptionStartGetStartedEvent
  | SubscriptionStartViewCampaignPageEvent
  | SubscriptionStartViewTidalComPageEvent;
